import { CreateApprovalDialog } from '@/shared/components/approvals/CreateApprovalDialog'
import { EditApprovalDialog } from '@/shared/components/approvals/EditApprovalDialog'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { useDeleteApproval, useDeleteApprovals } from '@/shared/hooks/approvals/useDelete'
import { useAreas } from '@/shared/hooks/useAreas'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { useDepartments } from '@/shared/hooks/useDepartments'
import { useRegions } from '@/shared/hooks/useRegions'
import { useGetUsers } from '@/shared/hooks/users/useGet'
import { api } from '@/shared/lib/api'
import { useCan } from '@/shared/lib/authorization'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { createColumnHelper } from '@tanstack/react-table'
import { ApprovalTemplate } from '@workspace/lib/api'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { Button } from '@workspace/ui/components/Button'
import { confirm } from '@workspace/ui/components/ConfirmDialog'
import { DataTable, DataTableSorting } from '@workspace/ui/components/DataTable'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
} from '@workspace/ui/components/Dialog'
import { Menu, MenuHeader, MenuItem, MenuPopover, MenuTrigger } from '@workspace/ui/components/Menu'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import dayjs from 'dayjs'
import { MoreHorizontal, Plus } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    regions: z.string().optional(),
    areas: z.string().optional(),
    departments: z.string().optional(),
    approvers: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
})

export const Route = createFileRoute('/approvals/')({
    validateSearch: searchSchema,
    component: ApprovalConfigurationPage,
    beforeLoad: () => requirePermissionBeforeLoad('view', 'system'),
})

const columnHelper = createColumnHelper<ApprovalTemplate>()

function ApprovalConfigurationPage() {
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const searchQuery = searchParams.q || ''
    const page = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : { sortBy: 'createdAt', sortDirection: 'desc' }
    const selectedRegions = searchParams.regions ? searchParams.regions.split(',') : []
    const selectedAreas = searchParams.areas ? searchParams.areas.split(',') : []
    const selectedDepartments = searchParams.departments ? searchParams.departments.split(',') : []
    const selectedApprovers = searchParams.approvers ? searchParams.approvers.split(',') : []

    const setSearchQuery = (q: string) => updateSearch({ q, page: 1 })
    const setPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }
    const setSelectedRegions = (regions: string[]) =>
        updateSearch({ regions: regions.length > 0 ? regions.join(',') : undefined, page: 1 })
    const setSelectedAreas = (areas: string[]) =>
        updateSearch({ areas: areas.length > 0 ? areas.join(',') : undefined, page: 1 })
    const setSelectedDepartments = (deps: string[]) =>
        updateSearch({ departments: deps.length > 0 ? deps.join(',') : undefined, page: 1 })
    const setSelectedApprovers = (apps: string[]) =>
        updateSearch({ approvers: apps.length > 0 ? apps.join(',') : undefined, page: 1 })

    const [rowSelection, setRowSelection] = useState({})

    const [openDialog, setOpenDialog] = useState(false)
    const [editDialog, setEditDialog] = useState<ApprovalTemplate | null>(null)
    const [waitingTasksWarning, setWaitingTasksWarning] = useState<{ count: number } | null>(null)

    const [openRegionFilter, setOpenRegionFilter] = useState(false)
    const [openAreaFilter, setOpenAreaFilter] = useState(false)
    const [openDepartmentFilter, setOpenDepartmentFilter] = useState(false)
    const [openApproverFilter, setOpenApproverFilter] = useState(false)

    // Debounce search
    const debouncedSearch = useDebounce(searchQuery, 300)

    const deleteMutation = useDeleteApproval()
    const bulkDeleteMutation = useDeleteApprovals()

    // Master data for filters
    const { data: regions } = useRegions({
        enabled: openRegionFilter || openAreaFilter,
    })

    const { data: areas } = useAreas({
        enabled: true,
    })

    const { data: departments } = useDepartments({
        enabled: openDepartmentFilter,
    })

    const { data: users } = useGetUsers({
        page: 1,
        pageSize: 100,
        enabled: openApproverFilter,
    })

    // Auto-prune area filter when region selection changes
    useEffect(() => {
        if (!areas?.data || selectedAreas.length === 0) return

        if (selectedRegions.length === 0) {
            updateSearch({ areas: undefined, page: 1 })
            return
        }

        const validAreaIds = new Set(
            areas.data.filter((a: any) => selectedRegions.includes(a.region?.id)).map((a: any) => a.id),
        )

        const newAreaFilter = selectedAreas.filter(id => validAreaIds.has(id))

        if (newAreaFilter.length !== selectedAreas.length) {
            updateSearch({
                areas: newAreaFilter.length > 0 ? newAreaFilter.join(',') : undefined,
                page: 1,
            })
        }
    }, [selectedRegions, selectedAreas, areas])

    // Fetch Approval Templates
    const { data: approvalTemplatesData, isLoading } = useQuery({
        queryKey: [
            'approval-configuration',
            page,
            pageSize,
            sorting,
            debouncedSearch,
            selectedRegions,
            selectedAreas,
            selectedDepartments,
            selectedApprovers,
        ],
        queryFn: () =>
            api.approval.list({
                page,
                take: pageSize,
                q: debouncedSearch || undefined,
                regionIds: selectedRegions.join(',') || undefined,
                areaIds: selectedAreas.join(',') || undefined,
                approvalDepartmentIds: selectedDepartments.join(',') || undefined,
                approverIds: selectedApprovers.join(',') || undefined,
                orderBy: sorting?.sortBy,
                order: sorting?.sortDirection?.toUpperCase() as 'ASC' | 'DESC' | undefined,
            }),
    })

    const executeDeleteOperation = async (action: (isConfirm?: boolean) => Promise<any>) => {
        try {
            await action()
        } catch (error: any) {
            const errorData = error.response?.data
            if (error.response?.status === 400 && errorData?.message === 'error.approvalConfigHasWaitingTasks') {
                const count = errorData.count ?? errorData.message?.count
                setWaitingTasksWarning({ count })
            } else if (
                error.response?.status === 409 &&
                errorData?.message === 'error.approvalConfigHasAffectedTasks'
            ) {
                const count = errorData.count ?? errorData.message?.count
                confirm({
                    title: t('common.confirmDialog.deleteTitle'),
                    description: t('approval.configuration.messages.deleteAffectedTasksConfirm', {
                        count,
                    }),
                    action: {
                        label: t('common.actions.delete'),
                        onClick: async () => {
                            await action(true)
                        },
                    },
                    cancel: {
                        label: t('common.actions.cancel'),
                    },
                })
            } else if (errorData?.message === 'error.approvalConfigCanBeDeleted') {
                await action(true)
            }
        }
    }

    const handleDelete = (id: string) => {
        confirm({
            title: t('common.confirmDialog.deleteTitle'),
            description: t('common.confirmDialog.deleteDescription'),
            action: {
                label: t('common.actions.delete'),
                onClick: () => executeDeleteOperation(isConfirm => deleteMutation.mutateAsync({ id, isConfirm })),
            },
            cancel: {
                label: t('common.actions.cancel'),
            },
            variant: 'destructive',
        })
    }

    const handleBulkDelete = async () => {
        const ids = Object.keys(rowSelection)
        if (ids.length === 0) return

        if (ids.length === 1) {
            return executeDeleteOperation(() => deleteMutation.mutateAsync({ id: ids[0] || '' }))
        }

        const templates = approvalTemplatesData?.data || []
        const selectedTemplates = templates.filter(t => ids.includes(t.id))

        try {
            // Check deletion status for all items
            const checks = await Promise.allSettled(ids.map(id => api.approval.delete(id, false)))

            const deletableIds: string[] = []
            const report: {
                canDelete: { id: string; name: string; isAffected: boolean }[]
                cannotDelete: { id: string; name: string; reason: 'waiting' | 'other' }[]
            } = { canDelete: [], cannotDelete: [] }

            checks.forEach((result, index) => {
                const id: string = ids[index] || ''
                const template = selectedTemplates.find(t => t.id === id) || { name: id }
                const name: string = template.name || id

                if (result.status === 'fulfilled') {
                    report.canDelete.push({ id, name, isAffected: false })
                } else {
                    const error = result.reason as any
                    const errorData = error.response?.data
                    const message =
                        typeof errorData?.message === 'string' ? errorData.message : errorData?.message?.message

                    if (error.response?.status === 409 && message === 'error.approvalConfigHasAffectedTasks') {
                        deletableIds.push(id)
                        report.canDelete.push({ id, name, isAffected: true })
                    } else if (error.response?.status === 400 && message === 'error.approvalConfigHasWaitingTasks') {
                        report.cannotDelete.push({ id, name, reason: 'waiting' })
                    } else if (message === 'error.approvalConfigCanBeDeleted') {
                        deletableIds.push(id)
                        report.canDelete.push({ id, name, isAffected: false })
                    }
                }
            })

            confirm({
                title: t('common.confirmDialog.deleteTitle'),
                description: (
                    <div className="flex flex-col gap-4 text-sm">
                        {report.canDelete.length > 0 && (
                            <div>
                                <div className="font-semibold mb-1">
                                    {t('approval.configuration.messages.bulkDeleteDeletable', {
                                        defaultValue: 'Danh sách có thể xóa:',
                                    })}
                                </div>
                                <ul className="list-none pl-0 space-y-1 max-h-[200px] overflow-y-auto">
                                    {report.canDelete.map(item => (
                                        <li key={item.id} className="text-muted-foreground flex items-center">
                                            <span className="mr-2">-</span>
                                            <span className="truncate">{item.name}</span>
                                            {item.isAffected && (
                                                <span className="ml-2 text-yellow-600 text-xs shrink-0">
                                                    (
                                                    {t('approval.configuration.messages.affectedTasks', {
                                                        defaultValue: 'Ràng buộc Task',
                                                    })}
                                                    )
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {report.cannotDelete.length > 0 && (
                            <div>
                                <div className="font-semibold mb-1">
                                    {t('approval.configuration.messages.bulkDeleteNonDeletable', {
                                        defaultValue: 'Danh sách không thể xóa:',
                                    })}
                                </div>
                                <ul className="list-none pl-0 space-y-1 max-h-[200px] overflow-y-auto">
                                    {report.cannotDelete.map(item => (
                                        <li key={item.id} className="text-muted-foreground flex items-center">
                                            <span className="mr-2">-</span>
                                            <span className="truncate">{item.name}</span>
                                            {item.reason === 'waiting' && (
                                                <span className="ml-2 text-destructive text-xs shrink-0">
                                                    (
                                                    {t('error.approvalConfigHasWaitingTasks', {
                                                        defaultValue: 'Đang chờ duyệt',
                                                    })}
                                                    )
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {report.canDelete.length === 0 && report.cannotDelete.length > 0 && (
                            <div className="mt-2 font-medium text-destructive">
                                {t('approval.configuration.messages.noItemsToDelete', {
                                    defaultValue: 'Không có bản ghi nào có thể xóa.',
                                })}
                            </div>
                        )}
                    </div>
                ),
                action:
                    report.canDelete.length > 0
                        ? {
                              label: t('common.actions.delete'),
                              onClick: async () => {
                                  await bulkDeleteMutation.mutateAsync({ ids: deletableIds, isConfirm: true })
                                  setRowSelection({})
                              },
                          }
                        : undefined,
                cancel: {
                    label: t('common.actions.cancel'),
                },
                variant: 'destructive',
            })
        } catch (error) {
            console.error('Bulk delete check failed', error)
        }
    }

    const columns = [
        columnHelper.display({
            id: 'actions1',
            header: '',
            size: 80,
            enableSorting: false,
            meta: {
                className: '!max-w-[46px] !min-w-[46px] !w-[46px]',
            },
            cell: ({ row }) => {
                const document = row.original

                return (
                    <MenuTrigger>
                        <Button variant="outline" size="iconSm" className="rounded-full border-foreground w-5 h-5">
                            <MoreHorizontal className="size-3!" />
                        </Button>
                        <MenuPopover className="min-w-[120px]">
                            <Menu>
                                <MenuHeader className="text-foreground">{t('common.actions.operation')}</MenuHeader>
                                <MenuItem
                                    onAction={() => {
                                        setEditDialog(document)
                                    }}
                                    className="cursor-pointer"
                                    isDisabled={!useCan('operate', 'system')}
                                >
                                    {t('common.actions.edit')}
                                </MenuItem>
                                <MenuItem
                                    onAction={() => handleDelete(document.id)}
                                    className="cursor-pointer text-destructive"
                                    isDisabled={!useCan('operate', 'system')}
                                >
                                    {t('common.actions.delete')}
                                </MenuItem>
                            </Menu>
                        </MenuPopover>
                    </MenuTrigger>
                )
            },
        }),
        columnHelper.accessor('name', {
            header: t('approval.configuration.table.name'),
            size: 250,
            enableSorting: true,
            cell: ({ row, getValue }) => (
                <span
                    className="font-medium text-sm text-foreground cursor-pointer"
                    onClick={() => setEditDialog(row.original)}
                >
                    {getValue()}
                </span>
            ),
        }),
        columnHelper.accessor('region.name', {
            header: t('approval.configuration.table.regionArea'),
            size: 200,
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex flex-col">
                    <span className="text-sm text-foreground">{row.original.region?.name || '-'}</span>
                    <span className="text-xs text-muted-foreground">{row.original.area?.name || '-'}</span>
                </div>
            ),
        }),
        columnHelper.accessor('approvalDepartment.name', {
            header: t('approval.configuration.table.department'),
            size: 200,
            enableSorting: false,
            cell: info => <span className="text-sm text-foreground">{info.getValue() || '-'}</span>,
        }),
        columnHelper.accessor('levels', {
            header: t('approval.configuration.table.approvers'),
            size: 200,
            enableSorting: false,
            cell: ({ getValue }) => {
                const levels = getValue() || []
                // Show users from all levels
                const allUsers = levels.flatMap(l => l.approvers)
                const uniqueUsers = Array.from(new Map(allUsers.map(u => [u.id, u])).values())
                const displayUsers = uniqueUsers.slice(0, 3)
                const remaining = uniqueUsers.length - 3

                return (
                    <div className="flex -space-x-2">
                        {displayUsers.map(user => (
                            <UserAvatar
                                key={user.id}
                                src={user.avatar}
                                alt={user.fullName}
                                fallback={user.fullName}
                                size="md"
                                className="border-2 border-background"
                            />
                        ))}
                        {remaining > 0 && (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background z-10">
                                +{remaining}
                            </div>
                        )}
                    </div>
                )
            },
        }),
        columnHelper.accessor('createdAt', {
            header: t('approval.configuration.table.createdAt'),
            size: 150,
            enableSorting: true,
            meta: {
                noTitleTooltip: true,
            },
            cell: info => {
                const formattedDate = dayjs(info.getValue()).format('DD/MM/YYYY HH:mm')
                return (
                    <span className="text-sm text-foreground" title={formattedDate}>
                        {formattedDate}
                    </span>
                )
            },
        }),
    ]

    const filterOptions = {
        regions: regions?.data.map(r => ({ id: r.id, name: r.name })) || [],
        areas: (() => {
            const allAreas = areas?.data || []
            if (selectedRegions.length === 0) return allAreas.map(a => ({ id: a.id, name: a.name }))
            return allAreas.filter(a => selectedRegions.includes(a.region?.id)).map(a => ({ id: a.id, name: a.name }))
        })(),
        departments: departments?.data.map(d => ({ id: d.id, name: d.name })) || [],
        users: users?.data.map(u => ({ id: u.id, name: u.fullName })) || [],
    }

    return (
        <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-150px)] min-h-[500px] overflow-hidden">
            <div className="shrink-0 flex items-center justify-between">
                <h1 className="text-2xl font-bold tracking-tight">{t('approval.configuration.title')}</h1>
                <Button onClick={() => setOpenDialog(true)} className="bg-yellow-400 text-black hover:bg-yellow-500">
                    <Plus className="mr-2 h-4 w-4" />
                    {t('approval.configuration.createDialog.title')}
                </Button>
            </div>

            <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                <div className="shrink-0 flex flex-wrap gap-4">
                    <BsSearchField
                        autoTrim
                        placeholder={t('common.search')}
                        value={searchQuery}
                        onChange={setSearchQuery}
                        className="w-[300px] h-10 bg-background"
                    />

                    <FilterDropdown
                        placeholder={t('approval.configuration.fields.region')}
                        options={filterOptions.regions}
                        selectedValues={selectedRegions}
                        onChange={setSelectedRegions}
                        setOpen={setOpenRegionFilter}
                    />
                    <FilterDropdown
                        placeholder={t('approval.configuration.fields.area')}
                        options={filterOptions.areas}
                        selectedValues={selectedAreas}
                        onChange={setSelectedAreas}
                        setOpen={setOpenAreaFilter}
                        isDisabled={selectedRegions.length === 0}
                        disabledTooltip={t('common.filters.requireRegionFirst')}
                    />
                    <FilterDropdown
                        placeholder={t('approval.configuration.fields.department')}
                        options={filterOptions.departments}
                        selectedValues={selectedDepartments}
                        onChange={setSelectedDepartments}
                        setOpen={setOpenDepartmentFilter}
                    />
                    <FilterDropdown
                        placeholder={t('approval.configuration.fields.approvers')}
                        options={filterOptions.users}
                        selectedValues={selectedApprovers}
                        onChange={setSelectedApprovers}
                        setOpen={setOpenApproverFilter}
                    />
                </div>

                <DataTable
                    data={approvalTemplatesData?.data || []}
                    columns={columns}
                    enableSorting
                    sorting={sorting}
                    setSorting={setSorting}
                    isLoading={isLoading}
                    emptyString={t('common.emptyState')}
                    enableRowSelection={true}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    containerClassName="flex-1 overflow-y-auto min-h-0"
                />
                <div className="shrink-0">
                    <TableDataFooter
                        currentPage={page}
                        pageSize={pageSize}
                        totalPages={approvalTemplatesData?.meta?.pageCount || 0}
                        totalCount={approvalTemplatesData?.meta?.itemCount || 0}
                        onPageChange={setPage}
                        onPageSizeChange={value => setPageSize(value || 10)}
                        selectedCount={Object.keys(rowSelection).length}
                        onDelete={handleBulkDelete}
                    />
                </div>
            </div>

            <CreateApprovalDialog open={openDialog} onOpenChange={setOpenDialog} />
            <EditApprovalDialog
                open={!!editDialog}
                onOpenChange={open => !open && setEditDialog(null)}
                value={editDialog}
            />

            <DialogOverlay isOpen={!!waitingTasksWarning} onOpenChange={open => !open && setWaitingTasksWarning(null)}>
                <DialogContent className="md:max-w-[425px]" isFullscreenOnMobile={false}>
                    <DialogHeader>
                        <DialogTitle>{t('common.confirmDialog.deleteTitle')}</DialogTitle>
                        <DialogDescription className="text-secondary-foreground font-medium text-sm">
                            {t('approval.configuration.messages.waitingTasksBlock', {
                                count: waitingTasksWarning?.count,
                            })}
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </DialogOverlay>
        </div>
    )
}
